import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Eye, EyeOff } from 'lucide-react';
import { useCollectiveInvestment } from '../context/CollectiveInvestmentContext';
import { useDraw } from '../context/DrawContext';
import { DrawHistoryScroll } from '../components/DrawHistoryScroll';
import { TimePickerModal } from '../components/TimePickerModal';
import { collection, query, orderBy, onSnapshot, doc, getDoc, writeBatch, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTelegramLink, createDrawNotification, getGeneralDonationData, type GeneralDonationData } from '../config/firebase';
import { 
  DrawState, 
  DrawResult
} from '../types/draw';
import { useDrawAdmin } from '../context/DrawAdminContext';
import { GeneralDonationDrawSection } from '../components/GeneralDonationDrawSection';
import { CollectiveInvestmentSection } from '../components/CollectiveInvestmentSection';
import { useGeneralDonationDraw } from '../context/GeneralDonationDrawContext';

const MAX_WINNERS = 10; // Maximum number of winners allowed

function formatCountdown(timeLeft: number): string {
  if (timeLeft <= 0) return "Le tirage peut commencer";
  
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  
  return `${hours}h ${minutes}m ${seconds}s`;
}

// Fonction utilitaire pour formater la date du tirage
function formatDrawDate(date: Date | undefined): string {
  if (!date) return '--';
  // Exemple : Mercredi 12/06/2024 à 20:00
  const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const jour = jours[date.getDay()];
  const jourNum = date.getDate().toString().padStart(2, '0');
  const mois = (date.getMonth() + 1).toString().padStart(2, '0');
  const annee = date.getFullYear();
  const heure = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${jour} ${jourNum}/${mois}/${annee} à ${heure}`;
}

export function Tirages() {
  const { 
    totalInvestment: projectedFunds, 
    participants: activeParticipants,
    resetInvestments
  } = useCollectiveInvestment();
  const { drawTime, setDrawTime } = useDraw();
  const { canSetDrawTime, canInitiateDraw } = useDrawAdmin();
  const { performGeneralDraw, totalParticipants, isGeneralDonationActive } = useGeneralDonationDraw();
  const [state, setState] = useState<DrawState>({
    winners: 0,
    countdown: "",
    isTimePickerOpen: false,
    canDraw: false,
    drawComplete: false,
    selectedWinners: [],
    prizePerWinner: 0,
    showFunds: false,
    drawHistory: [],
    telegramLink: 'https://t.me/magraine',
    isDrawing: false,
    participants: [],
    dgWinners: 0,
    dgPrizePerWinner: 0
  });
  const [showDG, setShowDG] = useState(false);
  const [donationData, setDonationData] = useState<GeneralDonationData | null>(null);

  useEffect(() => {
    if (!drawTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const timeLeft = drawTime.getTime() - now.getTime();

      if (timeLeft <= 0 && !state.canDraw) {
        setState(prev => ({ ...prev, canDraw: true, countdown: "Le tirage peut commencer" }));
        return;
      }

      if (timeLeft <= 0 && state.canDraw) {
        setState(prev => ({ ...prev, countdown: "" }));
        return;
      }
      
      setState(prev => ({ ...prev, countdown: formatCountdown(timeLeft) }));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [drawTime]);

  // Calculer automatiquement le prix par bénéficiaire pour le mode DG
  useEffect(() => {
    if (state.dgWinners > 0 && donationData) {
      const dgPrizeAmount = Math.floor(donationData.amount / state.dgWinners);
      setState(prev => ({ ...prev, dgPrizePerWinner: dgPrizeAmount }));
    } else {
      setState(prev => ({ ...prev, dgPrizePerWinner: 0 }));
    }
  }, [state.dgWinners, donationData]);

  // Calculer automatiquement le prix par bénéficiaire pour les fonds collectés
  useEffect(() => {
    if (state.winners > 0) {
      const prizeAmount = Math.floor(projectedFunds / state.winners);
      setState(prev => ({ ...prev, prizePerWinner: prizeAmount }));
    } else {
      setState(prev => ({ ...prev, prizePerWinner: 0 }));
    }
  }, [state.winners, projectedFunds]);

  const decreaseWinners = () => {
    if (state.winners > 0) {
      setState(prev => ({ ...prev, winners: prev.winners - 1 }));
    }
  };

  const increaseWinners = () => {
    // S'assurer que nous ne dépassons pas le nombre de participants actifs
    const maxPossibleWinners = Math.min(activeParticipants, MAX_WINNERS);
    if (state.winners < maxPossibleWinners) {
      setState(prev => ({ ...prev, winners: prev.winners + 1 }));
    }
  };

  // Fonctions spécifiques pour le mode DG
  const decreaseDGWinners = () => {
    if (state.dgWinners > 0) {
      setState(prev => ({ ...prev, dgWinners: prev.dgWinners - 1 }));
    }
  };

  const increaseDGWinners = () => {
    if (state.dgWinners < MAX_WINNERS) {
      setState(prev => ({ ...prev, dgWinners: prev.dgWinners + 1 }));
    }
  };

  // Vérifier si le tirage peut commencer (pour les deux modes)
  const canStartDraw = showDG 
    ? state.dgWinners > 0 && (donationData?.amount ?? 0) > 0
    : state.winners > 0 && activeParticipants >= state.winners && projectedFunds > 0;

  const performDGDraw = async () => {
    console.log('Début du tirage DG');
    console.log('Nombre de gagnants:', state.dgWinners);
    console.log('Donation active:', isGeneralDonationActive);
    console.log('Total participants:', totalParticipants);

    if (!state.dgWinners || !donationData?.amount) {
      console.log('Données manquantes:', { winners: state.dgWinners, amount: donationData?.amount });
      alert('Veuillez sélectionner le nombre de bénéficiaires pour la donation générale');
      return;
    }

    if (!isGeneralDonationActive || totalParticipants === 0) {
      console.log('État du tirage:', { isActive: isGeneralDonationActive, participants: totalParticipants });
      alert('Aucun participant éligible pour le tirage en mode DG');
      return;
    }

    try {
      // Démarrer l'animation
      setState(prev => ({ 
        ...prev, 
        isDrawing: true,
        selectedWinners: [] // Réinitialiser les gagnants
      }));

      const prizePerWinner = Math.floor((donationData?.amount || 0) / state.dgWinners);

      // Nettoyer l'ancien historique avant d'ajouter les nouveaux gagnants
      const batch = writeBatch(db);
      const oldDrawsRef = collection(db, 'drawHistory');
      const oldDrawsSnapshot = await getDocs(oldDrawsRef);
      oldDrawsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // Fonction pour ajouter un gagnant avec délai
      const addWinnerWithDelay = async (winners: Array<{ userId: string; amount: number; timestamp: Date }>, index: number) => {
        if (index >= winners.length) {
          // Tous les gagnants ont été ajoutés
          setState(prev => ({ 
            ...prev, 
            isDrawing: false,
            drawComplete: true
          }));

          // Mettre à jour la donation générale dans Firestore
          const batch = writeBatch(db);
          
          // Mettre à jour le document de la donation générale
          if (donationData?.id) {
            const donationRef = doc(db, 'generalDonations', donationData.id);
            batch.update(donationRef, {
              amount: 0,
              isActive: false,
              lastUpdated: new Date()
            });
          }

          await batch.commit();
          
          // Mettre à jour l'état local
          setDonationData(prev => prev ? {
            ...prev,
            amount: 0,
            isActive: false,
            lastUpdated: new Date()
          } : null);

          return;
        }

        const winner = {
          userId: winners[index].userId,
          amount: winners[index].amount,
          date: winners[index].timestamp
        };

        // Mettre à jour l'état avec le nouveau gagnant
        setState(prev => ({ 
          ...prev,
          selectedWinners: [...prev.selectedWinners, winner]
        }));

        // Sauvegarder le gagnant dans Firebase
        const batch = writeBatch(db);
        const drawResultRef = doc(collection(db, 'drawHistory'));
        
        const drawData = {
          winners: [...state.selectedWinners, winner],
          date: new Date(),
          totalAmount: donationData.amount,
          participantsCount: totalParticipants,
          isDGDraw: true // Marquer que c'est un tirage DG
        };

        batch.set(drawResultRef, drawData);

        // Créer la notification pour le gagnant
        try {
          await createDrawNotification(winner.userId, {
            drawId: drawResultRef.id,
            amount: winner.amount,
            isDGDraw: true
          });
          console.log('Notification créée pour:', winner.userId);
        } catch (error) {
          console.error('Erreur création notification:', error);
        }

        await batch.commit();

        // Attendre 3 secondes avant d'ajouter le prochain gagnant
        setTimeout(() => addWinnerWithDelay(winners, index + 1), 3000);
      };

      // Effectuer le tirage
      const winners = await performGeneralDraw(state.dgWinners, prizePerWinner);
      
      // Démarrer l'ajout des gagnants avec animation
      addWinnerWithDelay(winners, 0);

    } catch (error) {
      console.error('Erreur lors du tirage DG:', error);
      setState(prev => ({
        ...prev,
        isDrawing: false,
        drawComplete: false,
        selectedWinners: []
      }));
      alert('Une erreur est survenue lors du tirage');
    }
  };

  const performDraw = async () => {
    if (!state.winners || !projectedFunds) {
      alert('Veuillez sélectionner le nombre de gagnants et vérifier les fonds disponibles');
      return;
    }

    try {
      // Récupérer tous les participants
      const investmentRef = doc(db, 'collectiveInvestment', 'current');
      const docSnap = await getDoc(investmentRef);
      const participants = docSnap.data()?.participants || [];
      
      if (participants.length === 0) {
        alert('Aucun participant disponible pour le tirage');
        return;
      }

      // Démarrer l'animation
      setState(prev => ({ 
        ...prev, 
        isDrawing: true,
        selectedWinners: [] // Réinitialiser les gagnants
      }));

      // Sélection aléatoire des gagnants
      const tempParticipants = [...participants];
      const prizePerWinner = Math.floor(projectedFunds / state.winners);
      
      // Nettoyer l'ancien historique avant d'ajouter les nouveaux gagnants
      const batch = writeBatch(db);
      const oldDrawsRef = collection(db, 'drawHistory');
      const oldDrawsSnapshot = await getDocs(oldDrawsRef);
      oldDrawsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      // Fonction pour ajouter un gagnant avec délai
      const addWinnerWithDelay = async (index: number) => {
        if (index >= state.winners || tempParticipants.length === 0) {
          // Tous les gagnants ont été ajoutés
          setState(prev => ({ 
            ...prev, 
            isDrawing: false,
            drawComplete: true
          }));
          
          // Réinitialiser les investissements
          await resetInvestments();
          return;
        }

        const randomIndex = Math.floor(Math.random() * tempParticipants.length);
        const winnerId = tempParticipants[randomIndex];
        tempParticipants.splice(randomIndex, 1);
        
        const winner = {
          userId: winnerId,
          amount: prizePerWinner,
          date: new Date()
        };

        // Ajouter le nouveau gagnant à la liste existante
        setState(prev => ({ 
          ...prev,
          selectedWinners: [...prev.selectedWinners, winner]
        }));

        // Sauvegarder la liste complète des gagnants dans Firebase
        const batch = writeBatch(db);
        const drawResultRef = doc(collection(db, 'drawHistory'));
        
        const drawData = {
          winners: [...state.selectedWinners, winner],
          date: new Date(),
          totalAmount: projectedFunds,
          participantsCount: participants.length
        };

        batch.set(drawResultRef, drawData);

        // Créer la notification pour le gagnant
        try {
          await createDrawNotification(winner.userId, {
            drawId: drawResultRef.id,
            amount: winner.amount
          });
          console.log('Notification créée pour:', winner.userId);
        } catch (error) {
          console.error('Erreur création notification:', error);
        }

        await batch.commit();

        // Attendre 3 secondes avant d'ajouter le prochain gagnant
        setTimeout(() => addWinnerWithDelay(index + 1), 3000);
      };

      // Démarrer l'ajout des gagnants
      addWinnerWithDelay(0);

    } catch (error) {
      console.error('Erreur lors du tirage:', error);
      setState(prev => ({
        ...prev,
        isDrawing: false,
        drawComplete: false,
        selectedWinners: []
      }));
    }
  };

  // Charger l'historique des tirages
  useEffect(() => {
    const q = query(collection(db, 'drawHistory'), orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const history: DrawResult[] = snapshot.docs.map(doc => ({
        id: doc.id,
        winners: doc.data().winners,
        date: doc.data().date.toDate(),
        totalAmount: doc.data().totalAmount,
        participantsCount: doc.data().participantsCount
      }));
      setState(prev => ({ ...prev, drawHistory: history }));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchLink = async () => {
      try {
      const link = await getTelegramLink();
      if (link) {
          setState(prev => ({ ...prev, telegramLink: link }));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du lien Telegram:', error);
      }
    };
    fetchLink();
  }, []);

  useEffect(() => {
    const fetchDonationData = async () => {
      const data = await getGeneralDonationData();
      if (data) {
        setDonationData(data);
      }
    };
    fetchDonationData();
  }, []);

  // Ajouter cette condition pour le réglage du temps
  const handleTimeChange = (newTime: Date) => {
    if (canSetDrawTime) {
      setDrawTime(newTime);
    }
  };

  // Ajouter cette condition pour le lancement du tirage
  const handleDraw = async () => {
    if (!canInitiateDraw) return;
    
    if (showDG) {
      await performDGDraw();
    } else {
      await performDraw();
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-4 pt-6">
        {/* Collected Funds Section */}
        <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/10 mb-8">
          {/* Collected Funds Header */}
          <div className="flex items-center justify-between mb-4">
            {canSetDrawTime && (
              <button
                onClick={() => setState(prev => ({ ...prev, showFunds: !prev.showFunds }))}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {state.showFunds ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            )}
            {state.showFunds && (
              <button
                onClick={() => setState(prev => ({ ...prev, isTimePickerOpen: true }))}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Clock className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Collected Funds Content */}
          {state.showFunds && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">
                  {showDG ? "Donation générale" : "Fonds collectés"}
                </h2>
                <button
                  onClick={() => setShowDG(!showDG)}
                  className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                    showDG 
                      ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' 
                      : 'bg-white/5 hover:bg-white/10 text-gray-400'
                  }`}
                >
                  DG
                </button>
              </div>

              {showDG ? (
                <GeneralDonationDrawSection 
                  donationData={donationData} 
                  winners={state.dgWinners}
                  prizePerWinner={state.dgPrizePerWinner}
                  onIncreaseWinners={increaseDGWinners}
                  onDecreaseWinners={decreaseDGWinners}
                />
              ) : (
                <>
                  <CollectiveInvestmentSection 
                    projectedFunds={projectedFunds}
                    activeParticipants={activeParticipants}
                  />

                {/* Selection Mode */}
                <div className="space-y-1 mb-8">
                  <div className="bg-white/5 p-3 rounded-xl">
                    <div className="flex items-center justify-between mx-12">
                      <button 
                        onClick={decreaseWinners}
                        className="text-gray-500 hover:text-white transition-colors p-2"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      
                      <div className="flex-2 text-center">
                        <div className="text-2xl font-bold text-white">
                              {state.winners}
                        </div>
                        <div className="text-sm text-gray-400">
                          bénéficiaires
                        </div>
                      </div>
                      
                      <button 
                        onClick={increaseWinners}
                        className="text-gray-400 hover:text-white transition-colors p-2"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                      <div className="bg-white/5 p-2 rounded-xl">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">
                            {state.prizePerWinner.toLocaleString()} FCFA
                      </div>
                      <div className="text-sm text-gray-400">Prix/bénéficiaire</div>
                    </div>
                  </div>
                </div>
              </>
            )}
            </>
          )}
        </div>

        {/* Draw Status and Launch Button */}
        <div className="mt-5">
          {state.isDrawing ? (
            <div className="bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-8">
              <h2 className="text-2xl font-bold text-white text-center mb-4">TIRAGE EN COURS</h2>
              <div className="w-full h-1 bg-green-500/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500"
                  style={{
                    width: `${(state.selectedWinners.length / (showDG ? state.dgWinners : state.winners)) * 100}%`,
                    transition: 'width 0.5s ease-in-out'
                  } as React.CSSProperties}
                />
              </div>
              <p className="text-center text-green-400 font-bold mt-4">
                {state.selectedWinners.length === (showDG ? state.dgWinners : state.winners) ? 
                  "Félicitations aux gagnants !" : 
                  `Sélection du gagnant ${state.selectedWinners.length + 1}/${showDG ? state.dgWinners : state.winners}`
                }
              </p>
          </div>
          ) : (
            <div className="text-center space-y-1 pt-0">
          <h3 className="text-xl font-bold text-white">Tirage en direct</h3>
          
              <div className="flex items-center justify-center space-x-2 text-gray-400 mt-2">
                <span className="text-white">
                  {formatDrawDate(drawTime ?? undefined)}
                </span>
            <p>sur</p>
            <a 
                  href={state.telegramLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-400 hover:text-green-300 transition-colors"
            >
              Ce lien
            </a>
          </div>

              {((showDG && state.dgWinners > 0) || (!showDG && state.winners > 0)) ? (
                <button 
                  className={`w-full py-2 rounded-xl transition-colors ${
                    canStartDraw && state.canDraw
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : 'bg-gray-400/20 text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={!canStartDraw || !state.canDraw}
                  onClick={handleDraw}
                >
                  Lancer
                </button>
              ) : (
          <div className="text-3xl font-bold text-green-400 mt-4">
                  {state.countdown}
                </div>
              )}
          </div>
          )}
        </div>
      </div>

      {/* Section historique modifiée */}
      <div className="mt-8">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Historique des tirages</h2>
          {state.drawHistory.length > 0 && state.drawHistory[0]?.date && (
            <span className="text-gray-400">
              {new Date(state.drawHistory[0].date).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </span>
          )}
        </div>
        
        {/* Statistiques mises à jour */}
        {state.drawHistory.length > 0 && (
          <div className="text-sm text-gray-400 mb-4">
            <p>
              {/* Calculer le nombre total unique de gagnants sur tout l'historique */}
              {new Set(state.drawHistory.flatMap(draw => draw.winners.map(w => w.userId))).size} gagnants -
              {/* Calculer le montant total distribué sur tout l'historique */}
              {state.drawHistory
                .flatMap(draw => draw.winners)
                .reduce((total, winner) => total + winner.amount, 0)
                .toLocaleString()} FCFA distribués
            </p>
          </div>
        )}
        
        <div>
          {state.drawHistory.length === 0 ? (
            <p className="text-gray-400 text-center">Aucun tirage en cours</p>
          ) : (
            <DrawHistoryScroll history={state.drawHistory} />
          )}
        </div>
      </div>

      <TimePickerModal
        isOpen={state.isTimePickerOpen}
        onClose={() => setState(prev => ({ ...prev, isTimePickerOpen: false }))}
        onTimeSelect={handleTimeChange}
      />
    </div>
  );
}